import * as React from "react";
import { Alert, Card, Divider } from "antd";
import { CardTitle } from "../../../components/antd/card/CardTitle";
import { CardContent } from "../../../components/antd/card/CardContent";
import { FormItemDescription } from "../../../components/antd/form/FormItemDescription";
import { CardCollapsibleContent } from "../../../components/antd/card/CardCollapsibleContent";
import { PerformanceClasses } from "../../../schema/types/volumeSet/entity";
import { PerformanceClassReadonly } from "./uneditable/performanceClass";
import { FileSystemTypeReadonly } from "./uneditable/fileSystemType";
import { InitialCapacityReadonly } from "./uneditable/initialCapacity";
import { defaultValues } from "../../../services/defaults";
import { v4 as uuidv4 } from "uuid";
import { CopyButtonWithApproval } from "../../domain/create/copyButtonWithApproval";
import { SyntaxHighlight } from "../../../components/syntaxHighlight/SyntaxHighlight";
import { formSetValue, useCpFormError, useCpFormValue } from "../../gvc/testNewForm/utils";
import { CpRadioCardGroup } from "../../gvc/testNewForm/CpRadioCardGroup";
import { CpFormItem } from "../../gvc/testNewForm/CpFormItem";
import { CpLabel } from "../../gvc/testNewForm/CpLabel";
import { CpErrorWarning } from "../../gvc/testNewForm/CpErrorWarning";
import { useCpFormContext } from "../../../components/form/useCpFormContext";
import { CpAutoComplete } from "../../gvc/testNewForm/CpAutoComplete";
import { CpInput } from "../../gvc/testNewForm/CpInput";
import { CpSwitch } from "../../gvc/testNewForm/CpSwitch";
import { CpInputNumber } from "../../gvc/testNewForm/CpInputNumber";
import { CpSelect } from "../../gvc/testNewForm/CpSelect";
import { CpArrayObject } from "../../gvc/testNewForm/CpArrayObject";
import { CpArrayItemNameValuePair } from "../../workload/draft/CpArrayItemNameValuePair";
import { CpErrorWarningArrayIcon } from "../../gvc/testNewForm/CpErrorWarningArrayIcon";
import { VolumeSetSpecFileSystemType, FileSystemType } from "./fileSystemType";
import { CpLabelTooltip } from "../../gvc/testNewForm/CpLabelTooltip";
import { CpTooltip } from "../../gvc/testNewForm/CpTooltip";

interface Props {
  customEncAwsInstructions?: any;
  lockedFileSystemType?: FileSystemType;
}
export const Editable: React.FC<Props> = ({ customEncAwsInstructions, lockedFileSystemType }) => {
  const { id } = useCpFormContext();

  const df = defaultValues.volumeset;

  const isEdit = useCpFormValue(`meta.isEdit`);

  const fileSystemType = useCpFormValue("fileSystemType");
  const performanceClass = useCpFormValue(`performanceClass`);
  const initialCapacity = useCpFormValue(`initialCapacity`);
  const snapshotsIsEnabled = useCpFormValue("snapshots.isEnabled");
  const autoscalingIsEnabled = useCpFormValue("autoscaling.isEnabled");
  const predictiveIsEnabled = useCpFormValue("autoscaling.predictive.isEnabled");
  const autoscalingRootError = useCpFormError("autoscalingroot");
  const customEnc = useCpFormValue("customEncryption");

  React.useEffect(() => {
    try {
      if (performanceClass === "high-throughput-ssd" && Number(initialCapacity) < 1000) {
        formSetValue(id, `initialCapacity`, "1000");
      }
    } catch (e) {
      formSetValue(id, `initialCapacity`, "1000");
    }
  }, [performanceClass]);

  return (
    <>
      <div className="cpln-form-section cpln-ant-card-container">
        <Card>
          <CardTitle>
            Spec
            <CpErrorWarningArrayIcon
              names={[
                `initialCapacity`,
                `performanceClass`,
                `storageClassSuffix`,
                `fileSystemType`,
                `snapshots`,
                `autoscaling`,
                `mountOptions`,
                `customEncryption`,
              ]}
            />
          </CardTitle>
          <CardContent>
            {isEdit ? (
              <div className="flex flex-wrap gap-x-32 gap-y-8 mt-4 mb-8">
                <PerformanceClassReadonly />
                <FileSystemTypeReadonly />
                <InitialCapacityReadonly />
              </div>
            ) : (
              <>
                <VolumeSetSpecFileSystemType lockedValue={lockedFileSystemType} />
                {fileSystemType === "shared" ? null : (
                  <CpFormItem>
                    <CpLabel name={`performanceClass`}>Performance Class</CpLabel>
                    <CpRadioCardGroup
                      name={`performanceClass`}
                      options={PerformanceClasses.map((pc) => ({
                        label: pc.readableName,
                        value: pc.name,
                        children: (
                          <div style={{ minWidth: 200 }}>
                            <div className="text-sm">Min Capacity: {pc.minCapacity} GB</div>
                            <div className="text-sm">Max Capacity: {pc.maxCapacity} GB</div>
                          </div>
                        ),
                      }))}
                    />
                    <CpErrorWarning name={`performanceClass`} />
                  </CpFormItem>
                )}
                <CpFormItem>
                  <CpLabel name={`initialCapacity`}>Initial Capacity (GB)</CpLabel>
                  <CpAutoComplete
                    name={`initialCapacity`}
                    placeholder={`10`}
                    options={df.initialCapacity.selectOptions}
                    style={{ maxWidth: 200 }}
                  />
                  <CpErrorWarning name={`initialCapacity`} />
                </CpFormItem>
              </>
            )}
            <CpFormItem>
              <CpLabelTooltip>
                <CpLabel name={`storageClassSuffix`}>Storage Class Suffix</CpLabel>
                <CpTooltip
                  tooltipProps={{
                    title: `For self-hosted locations only. The storage class used for volumes in this set will be {performanceClass}-{fileSystemType}-{storageClassSuffix} if it exists, otherwise it will be {performanceClass}-{fileSystemType}`,
                  }}
                />
              </CpLabelTooltip>
              <CpInput name={`storageClassSuffix`} style={{ maxWidth: 600 }} />
              <CpErrorWarning name={`storageClassSuffix`} />
            </CpFormItem>
            <Divider orientation="left" orientationMargin={0}>
              Snapshots
            </Divider>
            {fileSystemType === "shared" ? (
              <div className="mb-8">
                <FormItemDescription>Snapshots are not available for Shared volume sets.</FormItemDescription>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <CpLabel name={`snapshots.isEnabled`}>Enable Snapshots</CpLabel>
                  <CpSwitch name={`snapshots.isEnabled`} />
                </div>
                {snapshotsIsEnabled ? (
                  <CpFormItem>
                    <CpLabelTooltip>
                      <CpLabel name={`snapshots.schedule`}>Schedule</CpLabel>
                      <CpTooltip tooltipProps={{ title: `Snapshots will only be taken if a schedule is set.` }} />
                    </CpLabelTooltip>
                    <CpInput name={`snapshots.schedule`} placeholder="0 0 1 * *" style={{ maxWidth: 600 }} />
                    <CpErrorWarning name={`snapshots.schedule`} />
                  </CpFormItem>
                ) : null}
                <div className="flex gap-2">
                  <CpFormItem>
                    <CpLabel name={`snapshots.retentionDuration.amount`}>Retention Duration</CpLabel>
                    <CpInputNumber name={`snapshots.retentionDuration.amount`} />
                    <CpErrorWarning name={`snapshots.retentionDuration.amount`} />
                  </CpFormItem>
                  <CpFormItem>
                    <CpLabel name={`snapshots.retentionDuration.unit`}>Unit</CpLabel>
                    <CpSelect
                      name={`snapshots.retentionDuration.unit`}
                      options={df.snapshots.retentionDuration.unit.selectOptions}
                      style={{ width: 100 }}
                    />
                    <CpErrorWarning name={`snapshots.retentionDuration.unit`} />
                  </CpFormItem>
                </div>
              </>
            )}
            <Divider orientation="left" orientationMargin={0}>
              Autoscaling
            </Divider>
            <div className="flex items-center gap-2">
              <CpLabel name={`autoscaling.isEnabled`}>Enable Autoscaling</CpLabel>
              <CpSwitch name={`autoscaling.isEnabled`} />
            </div>
            <CardCollapsibleContent collapsed={!autoscalingIsEnabled}>
              <CpFormItem>
                <CpLabelTooltip>
                  <CpLabel name={`autoscaling.maxCapacity`}>Max Capacity</CpLabel>
                  <CpTooltip tooltipProps={{ title: `A volume cannot grow to be bigger than this.` }} />
                </CpLabelTooltip>
                <CpInputNumber name={`autoscaling.maxCapacity`} addonAfter={`GB`} />
                <CpErrorWarning name={`autoscaling.maxCapacity`} />
              </CpFormItem>
              <CpFormItem>
                <CpLabelTooltip>
                  <CpLabel name={`autoscaling.minFreePercentage`}>Min Free Percentage</CpLabel>
                  <CpTooltip
                    tooltipProps={{
                      title: `The guaranteed free space on the volume as a percentage of the volume's total size. ControlPlane will
                  try to maintain at least that many percent free by scaling up the total size. E.g. 20.5 60`,
                    }}
                  />
                </CpLabelTooltip>
                <CpInputNumber name={`autoscaling.minFreePercentage`} />
                <CpErrorWarning name={`autoscaling.minFreePercentage`} />
              </CpFormItem>
              <CpFormItem>
                <CpLabelTooltip>
                  <CpLabel name={`autoscaling.scalingFactor`}>Scaling Factor</CpLabel>
                  <CpTooltip
                    tooltipProps={{
                      title: `When scaling is necessary, then new_capacity = current_capacity * storageScalingFactor. E.g. 1.5 3`,
                    }}
                  />
                </CpLabelTooltip>
                <CpInputNumber name={`autoscaling.scalingFactor`} />
                <CpErrorWarning name={`autoscaling.scalingFactor`} />
              </CpFormItem>
              <CpErrorWarning name={`autoscalingroot`} />
              <>
                <Divider orientation="left" orientationMargin={0}>
                  Predictive Scaling
                </Divider>
                <div className="flex items-center gap-2">
                  <CpLabel name={`autoscaling.predictive.isEnabled`}>Enable Predictive Scaling</CpLabel>
                  <CpSwitch name={`autoscaling.predictive.isEnabled`} disabled={!!autoscalingRootError} />
                </div>
                <CardCollapsibleContent collapsed={!predictiveIsEnabled}>
                  <CpFormItem>
                    <CpLabelTooltip>
                      <CpLabel name={`autoscaling.predictive.lookbackHours`}>Lookback Hours</CpLabel>
                      <CpTooltip
                        tooltipProps={{
                          title: `Hours of historical data to analyze. Default: 24. Max: 168 (1 week).`,
                        }}
                      />
                    </CpLabelTooltip>
                    <CpInputNumber name={`autoscaling.predictive.lookbackHours`} />
                    <CpErrorWarning name={`autoscaling.predictive.lookbackHours`} />
                  </CpFormItem>
                  <CpFormItem>
                    <CpLabelTooltip>
                      <CpLabel name={`autoscaling.predictive.projectionHours`}>Projection Hours</CpLabel>
                      <CpTooltip
                        tooltipProps={{
                          title: `Hours into the future to project storage needs. Default: 6.`,
                        }}
                      />
                    </CpLabelTooltip>
                    <CpInputNumber name={`autoscaling.predictive.projectionHours`} />
                    <CpErrorWarning name={`autoscaling.predictive.projectionHours`} />
                  </CpFormItem>
                  <CpFormItem>
                    <CpLabelTooltip>
                      <CpLabel name={`autoscaling.predictive.minDataPoints`}>Min Data Points</CpLabel>
                      <CpTooltip
                        tooltipProps={{
                          title: `Minimum data points required for reliable growth rate calculation. Default: 10.`,
                        }}
                      />
                    </CpLabelTooltip>
                    <CpInputNumber name={`autoscaling.predictive.minDataPoints`} />
                    <CpErrorWarning name={`autoscaling.predictive.minDataPoints`} />
                  </CpFormItem>
                  <CpFormItem>
                    <CpLabelTooltip>
                      <CpLabel name={`autoscaling.predictive.minGrowthRateGBPerHour`}>Min Growth Rate GB/Hour</CpLabel>
                      <CpTooltip
                        tooltipProps={{
                          title: `Minimum growth rate (GB/hour) to trigger predictive expansion. Default: 0.01.`,
                        }}
                      />
                    </CpLabelTooltip>
                    <CpInputNumber name={`autoscaling.predictive.minGrowthRateGBPerHour`} />
                    <CpErrorWarning name={`autoscaling.predictive.minGrowthRateGBPerHour`} />
                  </CpFormItem>
                  <CpFormItem>
                    <CpLabelTooltip>
                      <CpLabel name={`autoscaling.predictive.scalingFactor`}>Scaling Factor</CpLabel>
                      <CpTooltip
                        tooltipProps={{
                          title: `Scaling factor for predictive expansion. If not set, uses the parent autoscaling scaling factor.`,
                        }}
                      />
                    </CpLabelTooltip>
                    <CpInputNumber name={`autoscaling.predictive.scalingFactor`} />
                    <CpErrorWarning name={`autoscaling.predictive.scalingFactor`} />
                  </CpFormItem>
                </CardCollapsibleContent>
              </>
            </CardCollapsibleContent>
            {fileSystemType === "shared" ? (
              <>
                <Divider orientation="left" orientationMargin={0}>
                  Mount Options | Resources
                </Divider>
                <div className="mb-4">
                  <FormItemDescription>
                    For volume sets using the shared file system, specifies the CPU and memory resources allotted to
                    each mount point
                  </FormItemDescription>
                </div>
                <div className="flex gap-8">
                  <div className="flex gap-2">
                    <CpFormItem>
                      <CpLabel name={`mountOptions.minCpu.amount`}>Min CPU</CpLabel>
                      <CpInputNumber name={`mountOptions.minCpu.amount`} />
                      <CpErrorWarning name={`mountOptions.minCpu.amount`} />
                    </CpFormItem>
                    <CpFormItem>
                      <CpLabel name={`mountOptions.minCpu.unit`}>Unit</CpLabel>
                      <CpSelect
                        name={`mountOptions.minCpu.unit`}
                        options={[
                          { label: "Millicores", value: "m" },
                          { label: "Cores", value: "" },
                        ]}
                        style={{ width: 150 }}
                      />
                      <CpErrorWarning name={`mountOptions.minCpu.unit`} />
                    </CpFormItem>
                  </div>
                  <div className="flex gap-2">
                    <CpFormItem>
                      <CpLabel name={`mountOptions.maxCpu.amount`}>Max CPU</CpLabel>
                      <CpInputNumber name={`mountOptions.maxCpu.amount`} />
                      <CpErrorWarning name={`mountOptions.maxCpu.amount`} />
                    </CpFormItem>
                    <CpFormItem>
                      <CpLabel name={`mountOptions.maxCpu.unit`}>Unit</CpLabel>
                      <CpSelect
                        name={`mountOptions.maxCpu.unit`}
                        options={[
                          { label: "Millicores", value: "m" },
                          { label: "Cores", value: "" },
                        ]}
                        style={{ width: 150 }}
                      />
                      <CpErrorWarning name={`mountOptions.maxCpu.unit`} />
                    </CpFormItem>
                  </div>
                </div>
                <div className="flex gap-8">
                  <div className="flex gap-2">
                    <CpFormItem>
                      <CpLabel name={`mountOptions.minMemory.amount`}>Min Memory</CpLabel>
                      <CpInputNumber name={`mountOptions.minMemory.amount`} />
                      <CpErrorWarning name={`mountOptions.minMemory.amount`} />
                    </CpFormItem>
                    <CpFormItem>
                      <CpLabel name={`mountOptions.minMemory.unit`}>Unit</CpLabel>
                      <CpSelect
                        name={`mountOptions.minMemory.unit`}
                        options={[
                          { label: "Mi (Mebibytes)", value: "Mi" },
                          { label: "Gi (Gibibytes)", value: "Gi" },
                          { label: "K8s Memory", value: "k8s" },
                        ]}
                        style={{ width: 150 }}
                      />
                      <CpErrorWarning name={`mountOptions.minMemory.unit`} />
                    </CpFormItem>
                  </div>
                  <div className="flex gap-2">
                    <CpFormItem>
                      <CpLabel name={`mountOptions.maxMemory.amount`}>Max Memory</CpLabel>
                      <CpInputNumber name={`mountOptions.maxMemory.amount`} />
                      <CpErrorWarning name={`mountOptions.maxMemory.amount`} />
                    </CpFormItem>
                    <CpFormItem>
                      <CpLabel name={`mountOptions.maxMemory.unit`}>Unit</CpLabel>
                      <CpSelect
                        name={`mountOptions.maxMemory.unit`}
                        options={[
                          { label: "Mi (Mebibytes)", value: "Mi" },
                          { label: "Gi (Gibibytes)", value: "Gi" },
                          { label: "K8s Memory", value: "k8s" },
                        ]}
                        style={{ width: 150 }}
                      />
                      <CpErrorWarning name={`mountOptions.maxMemory.unit`} />
                    </CpFormItem>
                  </div>
                </div>
              </>
            ) : null}
            <Divider orientation="left" orientationMargin={0}>
              Custom Encryption
            </Divider>
            <CpArrayObject
              factoryFn={() => ({ id: uuidv4(), name: "", value: "" })}
              labels={["Region", "Key ID"]}
              name="customEncryption"
            >
              <CpArrayItemNameValuePair />
            </CpArrayObject>
            {customEnc.length > 0 && customEncAwsInstructions !== null ? (
              <div className="mb-10 mt-4">
                <div className="font-semibold">AWS Custom Encryption Instructions</div>
                <div className="flex items-center gap-2">
                  <div>Add these statements to the Key Policy document for every KMS Key</div>
                  <CopyButtonWithApproval value={customEncAwsInstructions} />
                </div>
                <Alert className="my-2" type={`info`} message="Replace {REGION} placeholder with the real value" />
                <SyntaxHighlight
                  code={customEncAwsInstructions}
                  language="json"
                  fontSize={12}
                />
              </div>
            ) : null}
          </CardContent>
        </Card>
      </div>
    </>
  );
};
